/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 处理待提取照片的自定义Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-01 10:59:11
 */

import React, { useCallback, useRef } from 'react'
import { useWatermarkExtraction } from './useWatermarkExtraction'
import {
  PhotoItem,
  WatermarkItem,
  WatermarkExtractionStatus
} from '../context/WatermarkBatchContext'

/**
 * 处理待提取照片的自定义Hook
 * @param photosRef 照片列表的引用，用于在回调中获取最新状态
 * @param setPhotoList 更新照片列表的函数
 * @param updatePhotosStatus 更新照片状态的函数
 * @param setCurrentIndex 设置当前选中照片索引的函数
 * @returns 处理待提取照片的函数
 */
export const usePendingPhotosProcessor = (
  photosRef: React.MutableRefObject<PhotoItem[]>,
  setPhotoList: React.Dispatch<React.SetStateAction<PhotoItem[]>>,
  updatePhotosStatus: (
    photosToUpdate: PhotoItem[],
    status: WatermarkExtractionStatus
  ) => void,
  setCurrentIndex: (index: number) => void,
  setExtracting?: (isExtracting: boolean, photoCount?: number) => void,
  shouldAutoSelect?: () => boolean
) => {
  // 使用水印提取 hook
  const { extractWatermarks } = useWatermarkExtraction()

  // 处理待提取照片的函数
  const processPendingPhotos = useCallback(
    (pendingPhotos: PhotoItem[]) => {
      if (pendingPhotos.length === 0) return

      // 定义照片状态更新回调
      const handlePhotosStatusUpdate = (
        photosToUpdate: PhotoItem[],
        status: WatermarkExtractionStatus
      ) => {
        updatePhotosStatus(photosToUpdate, status)
      }

      // 定义水印数据更新回调
      const handleWatermarkDataUpdate = (
        photoUrl: string,
        watermarkID: string,
        watermarkData: WatermarkItem[]
      ) => {
        // 使用 ref 获取最新的 photos 状态
        const currentPhotos = photosRef.current

        // 找到所有匹配的照片索引
        const matchingIndices: number[] = []
        currentPhotos.forEach((photo, index) => {
          if (photo.smallURL === photoUrl) {
            matchingIndices.push(index)
          }
        })

        if (matchingIndices.length > 0) {
          // 更新所有匹配的照片
          setPhotoList((prevPhotos) => {
            const updatedPhotos = [...prevPhotos]
            const updatedPhotoItems: PhotoItem[] = []

            matchingIndices.forEach((index) => {
              const photo = updatedPhotos[index]
              const updatedPhoto = {
                ...photo,
                watermarkID: watermarkID,
                watermarkData: watermarkData,
                watermarkStatus: WatermarkExtractionStatus.COMPLETED // 设置为已完成
              }
              updatedPhotos[index] = updatedPhoto
              updatedPhotoItems.push(updatedPhoto)
            })

            return updatedPhotos
          })

          // 同时更新PhotoPicker中的照片状态，传递包含水印数据的完整照片对象
          // 使用setTimeout避免在渲染过程中更新其他组件
          setTimeout(() => {
            matchingIndices.forEach((index) => {
              const photo = currentPhotos[index]
              const updatedPhoto = {
                ...photo,
                watermarkData: watermarkData,
                watermarkID: watermarkID,
                watermarkStatus: WatermarkExtractionStatus.COMPLETED
              }
              updatePhotosStatus(
                [updatedPhoto],
                WatermarkExtractionStatus.COMPLETED
              )
            })
          }, 0)
        }
      }

      // 定义提取完成回调，选中最后一个成功提取的照片
      const handleExtractionComplete = (successPhotos: PhotoItem[]) => {
        // 只有在没有当前选中照片时，才允许提取完成后自动选中
        if (shouldAutoSelect && !shouldAutoSelect()) {
          return
        }

        // 获取最新的照片列表
        const currentPhotos = photosRef.current

        // 找到最后一个成功提取的照片在照片列表中的索引
        // 从后往前查找，找到最后一个匹配的成功照片
        let lastSuccessIndex = -1
        if (successPhotos.length > 0) {
          for (let i = currentPhotos.length - 1; i >= 0; i--) {
            const photo = currentPhotos[i]
            const isSuccessPhoto = successPhotos.some(
              (successPhoto) => successPhoto.smallURL === photo.smallURL
            )
            if (
              isSuccessPhoto &&
              photo.watermarkStatus === WatermarkExtractionStatus.COMPLETED
            ) {
              lastSuccessIndex = i
              break
            }
          }
        }

        // 如果找到了最后一个成功照片的索引，选中它
        if (lastSuccessIndex !== -1) {
          setCurrentIndex(lastSuccessIndex)
        } else {
          // 如果找不到成功照片，则选中本次处理的最后一个照片
          // 从后往前查找，找到最后一个在 pendingPhotos 中的照片
          let lastPendingIndex = -1
          for (let i = currentPhotos.length - 1; i >= 0; i--) {
            const photo = currentPhotos[i]
            const isPendingPhoto = pendingPhotos.some(
              (pendingPhoto) => pendingPhoto.smallURL === photo.smallURL
            )
            if (isPendingPhoto) {
              lastPendingIndex = i
              break
            }
          }

          // 如果找到了本次处理的最后一个照片，选中它
          if (lastPendingIndex !== -1) {
            setCurrentIndex(lastPendingIndex)
          }
        }
      }

      // 异步执行水印提取，不阻塞UI
      extractWatermarks(
        pendingPhotos,
        handlePhotosStatusUpdate,
        handleWatermarkDataUpdate,
        handleExtractionComplete,
        setExtracting
          ? (photoCount: number) => setExtracting(true, photoCount)
          : undefined,
        setExtracting ? () => setExtracting(false, 0) : undefined
      )
    },
    [
      extractWatermarks,
      updatePhotosStatus,
      setPhotoList,
      setCurrentIndex,
      setExtracting,
      shouldAutoSelect
    ]
  )

  return { processPendingPhotos }
}
